// CTV-466 follow-up: standalone "Edit commercial" panel rendered inside
// EditCampaignModal. Replaces the legacy single-file SwapCreativeTabPanel
// with a UI that mirrors the wizard's commercial step — same multi-slot
// scheduler, same "add another commercial" affordance, same library picker,
// same per-slot CreativeSlotCard so every commercial reads as a peer
// regardless of whether it's already running or a new replacement awaiting
// review. Save POSTs the diff to /submissions/:id/update-creative-set;
// new commercials land as pending_review and the publisher's per-creative
// approve flow (CTV-457 Phase C) takes over from there. On approval a
// creative_swap push job updates the running Publica line items.
//
// Waymark: the wizard still owns the full Waymark account/generation
// lifecycle (WaymarkProvider). Lifting that provider into the modal context
// was deferred; CTV-611 instead added useWaymarkRework, a minimal
// login-for-creative client (no provider lifting), so a rejected Waymark
// commercial can be reopened, reworked, and routed back through this
// panel's upload path. File upload and library pick remain the other two
// replacement paths.

import React from 'react';
import { Loader2, Plus } from 'lucide-react';
import type {
  CreativeSlotState,
  PublisherConfig,
  SelfServiceCreativeRow,
  SelfServiceSubmissionRow,
} from '../../types/selfService';
import { apiClient } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import { buildStationPalette } from '../../lib/dateAxisRendering';
import {
  rederiveSegmentSharesOnDateChange,
  reseatSharesAfterRemoval,
  resetToEven,
  seedSegmentSharesForNewCreative,
  singleSegmentShareForWindow,
  type CreativeWindow,
} from './shared/rotationSegments';
import { useReusableLibrary, priorApprovalBadge } from './shared/useReusableLibrary';
import { REJECTION_REASONS_BY_CODE } from './rejectionReasons';
import { useWaymarkRework } from './waymark/useWaymarkRework';
import MultiCreativeScheduler from './scheduler/MultiCreativeScheduler';
import CreativeSlotCard from './scheduler/CreativeSlotCard';
import LibraryPickerModal, { type LibraryPick } from './steps/LibraryPickerModal';
import PrimaryButton from './shared/PrimaryButton';
import Banner from './shared/Banner';
import CoverageBanner from './shared/CoverageBanner';
import ConcurrencyBanner from './shared/ConcurrencyBanner';
import { validateCreativeCoverage } from './shared/creativeCoverage';
import { validateCreativeConcurrency } from './shared/creativeConcurrency';

interface EditCommercialTabPanelProps {
  submission: SelfServiceSubmissionRow;
  cfg: PublisherConfig;
  advertiserId: string | null;
  onSubmitted: () => void;
}

// Working-slot shape — mirrors CreativeSlotState plus a `existingCreativeId`
// that the save handler uses to split the local set into the
// updateCreativeSet diff (updateExisting vs. addCreatives).
interface EditSlot extends CreativeSlotState {
  // null = freshly added in this session; a string id = an existing
  // selfservice_creatives row we're editing in place.
  existingCreativeId: string | null;
  // For existing creatives, the row's status so the pill can distinguish
  // 'Running' (active) from 'Awaiting review' (pending_review). null on
  // freshly added slots.
  existingStatus: 'active' | 'pending_review' | null;
  // CTV-611: true when this slot was created through the recovery block's
  // fix actions (upload / library pick / Waymark rework). Drives the
  // rotation note; the slot otherwise behaves as a normal add.
  isFix?: boolean;
  // CTV-611: set on Waymark-rework fix slots so the new creative row keeps
  // the voided row's draft pointer (addCreatives[].sourceDraftId).
  sourceDraftId?: string | null;
}

// CTV-487: "today" as YYYY-MM-DD in local time. Matches the API's
// truncationDate calculation (priorDateOnly(toDateOnlyString(new Date())))
// closely enough for the Gantt preview — exact agreement isn't required
// because the API recomputes server-side at save.
function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function priorYmd(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const prev = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) - 1));
  const y = prev.getUTCFullYear();
  const mo = String(prev.getUTCMonth() + 1).padStart(2, '0');
  const d = String(prev.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

function maxYmd(a: string, b: string): string {
  return a > b ? a : b;
}

function makeSlotId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `edit-slot-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function fromCreative(c: SelfServiceCreativeRow): EditSlot {
  const segmentShares =
    Array.isArray(c.segment_shares) && c.segment_shares.length > 0
      ? c.segment_shares.map((s) => ({
          startDate: s.start_date,
          endDate: s.end_date,
          share: s.share,
        }))
      : singleSegmentShareForWindow(c.creative_start_date, c.creative_end_date);
  return {
    slotId: makeSlotId(),
    existingCreativeId: c.id,
    existingStatus: c.status === 'pending_review' ? 'pending_review' : 'active',
    storagePath: c.storage_path,
    filename: c.filename,
    mimeType: c.mime_type ?? 'video/mp4',
    sizeBytes: c.size_bytes ?? 0,
    clickThroughUrl: c.click_through_url ?? '',
    segmentShares,
    startDate: c.creative_start_date,
    endDate: c.creative_end_date,
  };
}

function toRotationWindows(slots: EditSlot[]): CreativeWindow[] {
  return slots.map((s) => ({
    id: s.slotId,
    startDate: s.startDate,
    endDate: s.endDate,
    segmentShares: s.segmentShares.map((seg) => ({ ...seg })),
  }));
}

function applyDerivedShares(slots: EditSlot[], derived: CreativeWindow[]): EditSlot[] {
  const byId = new Map(derived.map((d) => [d.id, d]));
  return slots.map((s) => {
    const d = byId.get(s.slotId);
    if (!d) return s;
    return {
      ...s,
      startDate: d.startDate,
      endDate: d.endDate,
      segmentShares: d.segmentShares.map((seg) => ({ ...seg })),
    };
  });
}

const EditCommercialTabPanel: React.FC<EditCommercialTabPanelProps> = ({
  submission,
  cfg,
  advertiserId,
  onSubmitted,
}) => {
  const accent = cfg.accentColor || cfg.primaryColor;
  const campaignStartDate = submission.campaign?.startDate ?? '';
  const campaignEndDate = submission.campaign?.endDate ?? '';

  const initialSlots = React.useMemo<EditSlot[]>(
    () =>
      (submission.creatives ?? [])
        .filter((c) => c.status === 'active' || c.status === 'pending_review')
        .map(fromCreative),
    [submission.creatives],
  );

  const [slots, setSlots] = React.useState<EditSlot[]>(initialSlots);
  // CTV-466 follow-up: mirror the backend's ADR-0007 prior-approval predicate
  // in the UI so newly added commercials whose (filename, size_bytes)
  // match a previously-approved entry in the station/advertiser library
  // render as "Pre-approved" instead of "Awaiting review". Lookup is by
  // exact (filename, sizeBytes) — same key the server uses in
  // hasPriorApproval — so the pill matches what updateCreativeSet will
  // actually do on save.
  const { items: libraryItems } = useReusableLibrary(cfg.slug, advertiserId);
  const priorApprovalByKey = React.useMemo(() => {
    const out = new Map<string, 'approved' | 'rejected'>();
    for (const it of libraryItems) {
      const badge = priorApprovalBadge(it);
      if (!badge) continue;
      const key = `${it.filename}::${it.size_bytes ?? ''}`;
      // Library is sorted newest-first; only record the first verdict per key.
      if (!out.has(key)) out.set(key, badge);
    }
    return out;
  }, [libraryItems]);
  // Track which original creative ids the user has removed during this
  // session so the save handler can include them in removeCreativeIds.
  const [removedSourceIds, setRemovedSourceIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [files, setFiles] = React.useState<Record<string, File | null>>({});
  const [uploadingSlotId, setUploadingSlotId] = React.useState<string | null>(null);
  const [pickerSlotId, setPickerSlotId] = React.useState<string | null>(null);
  // CTV-611: the recovery block's "Choose from library" opens the same
  // LibraryPickerModal, but the fix slot is only created once a pick lands
  // (deferred fix slot), so it needs its own open flag rather than a slotId.
  const [fixPickerOpen, setFixPickerOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  // A slot only "exists" for the scheduler, palette, coverage, and badge once
  // it has a file picked or uploaded — mirrors UploadCreativeStep's
  // slotHasContent. Empty slots stay rendered as upload cards (with the
  // Remove affordance) but are otherwise hidden from rotation math so the
  // gantt doesn't surface a phantom bar / "awaiting review" pill before the
  // advertiser drops a file.
  const slotHasContent = React.useCallback(
    (s: EditSlot) => !!s.storagePath || !!s.filename,
    [],
  );
  const visibleSlots = React.useMemo(() => slots.filter(slotHasContent), [slots, slotHasContent]);

  // CTV-487: on a running campaign, only the rest-of-campaign window is
  // actionable. Past days already delivered (or didn't) — the buyer can't
  // retroactively cover them. Clamp the coverage / concurrency window to
  // max(campaignStart, today) so save-time validation only blocks on gaps
  // that affect future delivery. On not-yet-approved campaigns the
  // campaign hasn't started yet, so the original window is used as-is.
  const coverageWindowStart =
    submission.status === 'approved' && campaignStartDate && campaignStartDate < todayYmd()
      ? todayYmd()
      : campaignStartDate;

  // ADR-0006 per-day creative coverage on the post-diff state. Also remember
  // whether the gap pre-existed when the modal opened so the banner copy can
  // soften ("This campaign already had uncovered days…") for legacy state.
  // Coverage is computed from visibleSlots only so an empty extra slot
  // doesn't spuriously trip the banner.
  const initialCoverage = React.useMemo(
    () =>
      validateCreativeCoverage({
        windowStart: coverageWindowStart,
        windowEnd: campaignEndDate,
        creatives: initialSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [coverageWindowStart, campaignEndDate, initialSlots],
  );
  const coverage = React.useMemo(
    () =>
      validateCreativeCoverage({
        windowStart: coverageWindowStart,
        windowEnd: campaignEndDate,
        creatives: visibleSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [coverageWindowStart, campaignEndDate, visibleSlots],
  );
  const preExistingGap = !initialCoverage.covered;

  // ADR-0008 per-day concurrency cap on the post-diff state. Mirrors the
  // pre-existing-gap softening: if the violation already existed when the
  // modal opened, the banner softens its copy instead of accusing the buyer
  // of just-now creating it.
  const initialConcurrency = React.useMemo(
    () =>
      validateCreativeConcurrency({
        windowStart: coverageWindowStart,
        windowEnd: campaignEndDate,
        creatives: initialSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [coverageWindowStart, campaignEndDate, initialSlots],
  );
  const concurrency = React.useMemo(
    () =>
      validateCreativeConcurrency({
        windowStart: coverageWindowStart,
        windowEnd: campaignEndDate,
        creatives: visibleSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [coverageWindowStart, campaignEndDate, visibleSlots],
  );
  const preExistingOverCap = !initialConcurrency.withinCap;

  // CTV-487 / ADR-0012: client-side mirror of the API's second coverage
  // pass. Runs over the ACTIVE-ONLY projected Live Set — existing actives
  // (with their drag-applied dates), new prior-approved adds (auto-active),
  // and excludes pending_review adds. Removed creatives are treated as
  // truncated to "today − 1" so the preview Gantt matches what Publica
  // will actually deliver post-save.
  const isApproved = submission.status === 'approved';
  const pausedPreviewCoverage = React.useMemo(() => {
    if (!isApproved) return { covered: true, uncoveredRanges: [] as Array<{ startDate: string; endDate: string }> };
    const truncateDate = priorYmd(todayYmd());
    const ranges: Array<{ startDate: string; endDate: string }> = [];
    // Existing active rows still in the Live Set.
    for (const s of visibleSlots) {
      if (s.existingCreativeId == null) continue;
      if (s.existingStatus !== 'active') continue;
      ranges.push({ startDate: s.startDate, endDate: s.endDate });
    }
    // Existing active rows the buyer just removed in this session — treated
    // as truncated to "today − 1" on save.
    for (const sourceId of removedSourceIds) {
      const orig = (submission.creatives ?? []).find((c) => c.id === sourceId);
      if (!orig || orig.status !== 'active') continue;
      const originalEnd = orig.creative_end_date;
      const truncated = originalEnd < truncateDate ? originalEnd : truncateDate;
      if (truncated >= orig.creative_start_date) {
        ranges.push({ startDate: orig.creative_start_date, endDate: truncated });
      }
    }
    // New adds whose (filename, size) match an approved library entry → auto-active.
    for (const s of visibleSlots) {
      if (s.existingCreativeId != null) continue;
      const key = `${s.filename}::${s.sizeBytes ?? ''}`;
      if (priorApprovalByKey.get(key) === 'approved') {
        ranges.push({ startDate: s.startDate, endDate: s.endDate });
      }
    }
    return validateCreativeCoverage({
      windowStart: coverageWindowStart,
      windowEnd: campaignEndDate,
      creatives: ranges,
    });
  }, [
    isApproved,
    visibleSlots,
    removedSourceIds,
    submission.creatives,
    priorApprovalByKey,
    coverageWindowStart,
    campaignEndDate,
  ]);
  const willPauseDelivery =
    isApproved && coverage.covered && !pausedPreviewCoverage.covered;

  // CTV-487 / CTV-606 (ported in CTV-611): Restore only makes sense when the
  // campaign is actually in Paused Delivery. If any active creative row still
  // extends into `[today .. campaignEnd]` its 100% share would collide with
  // the restored row's 100% share (assertSegmentSharesValid rejects the save
  // with a generic "We couldn't save the schedule" 400). Suppress Restore
  // whenever a delivering active is present. The canonical Restore case is a
  // rejected or removed pending swap that left the Live Set with no active
  // covering today. The restored row inherits the prior file's fingerprint
  // and lands `active` via the ADR-0007 prior-approval predicate,
  // immediately enqueuing a swap push.
  const restorable = React.useMemo(() => {
    if (!isApproved) return null;
    const today = todayYmd();
    const activeCoversRestoreWindow = (submission.creatives ?? []).some(
      (c) => c.status === 'active' && c.creative_end_date >= today,
    );
    if (activeCoversRestoreWindow) return null;
    const retiredAll = (submission.creatives ?? []).filter((c) => c.status === 'retired');
    if (retiredAll.length === 0) return null;
    // Pick the most recently retired (largest creative_end_date wins; ties
    // break on created_at). Mirrors "last creative the buyer removed".
    const candidate = [...retiredAll].sort((a, b) => {
      if (a.creative_end_date !== b.creative_end_date) {
        return a.creative_end_date < b.creative_end_date ? 1 : -1;
      }
      return b.created_at.localeCompare(a.created_at);
    })[0];
    // Don't surface restore if the buyer already has a slot for this fingerprint.
    const alreadyPresent = visibleSlots.some(
      (s) => s.filename === candidate.filename && (s.sizeBytes ?? 0) === (candidate.size_bytes ?? 0),
    );
    if (alreadyPresent) return null;
    return candidate;
  }, [isApproved, submission.creatives, visibleSlots]);

  // CTV-487 follow-up: pending_review creatives held at 100% share collide
  // with the restored creative's 100% share over the overlap window, busting
  // the cross-creative shares-sum-to-100 invariant. Withdraw all pending
  // creatives in the same atomic save — semantically "the buyer aborted their
  // own change, bring back the previous file". The retire path on the server
  // already handles pending_review IDs in removeCreativeIds.
  const pendingForRestore = React.useMemo(
    () => (submission.creatives ?? []).filter((c) => c.status === 'pending_review'),
    [submission.creatives],
  );

  // CTV-611 / ADR-0023: unresolved rejection = voided AND resolved_at null.
  // Drives the recovery block at the top of the panel. resolved_at is
  // stamped server-side on every open voided row the moment any recovery
  // add lands, so a handled rejection never re-renders the block.
  const unresolvedRejections = React.useMemo(
    () => (submission.creatives ?? []).filter((c) => c.status === 'voided' && !c.resolved_at),
    [submission.creatives],
  );
  // The newest rejection owns the context card and seeds the deferred fix
  // slot's window when there are several open rejections.
  const fixSource = React.useMemo(() => {
    if (unresolvedRejections.length === 0) return null;
    return [...unresolvedRejections].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  }, [unresolvedRejections]);

  // CTV-611: is delivery paused right now? Same active-only-coverage-of-today
  // concept the detail page's "Delivery paused" banner derives, computed from
  // the stored rows (not the in-session slots) because it describes the
  // campaign's current state, not the post-save preview.
  const pausedNow = React.useMemo(() => {
    if (!isApproved) return false;
    const today = todayYmd();
    const activeRanges = (submission.creatives ?? [])
      .filter((c) => c.status === 'active')
      .map((c) => ({ startDate: c.creative_start_date, endDate: c.creative_end_date }));
    const cov = validateCreativeCoverage({
      windowStart: campaignStartDate,
      windowEnd: campaignEndDate,
      creatives: activeRanges,
    });
    return cov.uncoveredRanges.some((r) => r.startDate <= today && r.endDate >= today);
  }, [isApproved, submission.creatives, campaignStartDate, campaignEndDate]);

  const onRestorePrevious = async () => {
    if (!restorable) return;
    setSaving(true);
    setError(null);
    try {
      const today = todayYmd();
      const startDate = maxYmd(today, campaignStartDate);
      await apiClient.post(`/submissions/${submission.id}/update-creative-set`, {
        removeCreativeIds: pendingForRestore.map((c) => c.id),
        addCreatives: [
          {
            filename: restorable.filename,
            storagePath: restorable.storage_path,
            mimeType: restorable.mime_type ?? 'video/mp4',
            sizeBytes: restorable.size_bytes ?? 0,
            clickThroughUrl: restorable.click_through_url ?? undefined,
            segmentShares: [
              { startDate, endDate: campaignEndDate, share: 100 },
            ],
            startDate,
            endDate: campaignEndDate,
          },
        ],
        updateExisting: [],
      });
      onSubmitted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to restore previous commercial');
    } finally {
      setSaving(false);
    }
  };

  // CTV-457: positional palette indexed against visibleSlots so newly added
  // empty cards don't bump everyone's colour. Empty slots fall back to a
  // muted slate token in render.
  const paletteSize = Math.max(visibleSlots.length, 6);
  const palette = React.useMemo(
    () => buildStationPalette(cfg.primaryColor, cfg.secondaryColor, paletteSize),
    [cfg.primaryColor, cfg.secondaryColor, paletteSize],
  );
  const visibleColors = React.useMemo(
    () => Array.from({ length: visibleSlots.length }, (_, i) => palette[i % palette.length]),
    [palette, visibleSlots.length],
  );
  const visiblePosition = React.useCallback(
    (slotId: string) => visibleSlots.findIndex((s) => s.slotId === slotId),
    [visibleSlots],
  );

  // Detect whether anything actually changed against the initial set — drives
  // the Save button's disabled state.
  const hasChanges = React.useMemo(() => {
    if (removedSourceIds.size > 0) return true;
    if (slots.some((s) => s.existingCreativeId == null)) return true;
    const byId = new Map(slots.map((s) => [s.existingCreativeId, s]));
    return initialSlots.some((init) => {
      const current = byId.get(init.existingCreativeId);
      if (!current) return true;
      return (
        JSON.stringify(current.segmentShares) !== JSON.stringify(init.segmentShares) ||
        current.startDate !== init.startDate ||
        current.endDate !== init.endDate ||
        (current.clickThroughUrl || '') !== (init.clickThroughUrl || '')
      );
    });
  }, [slots, removedSourceIds, initialSlots]);

  // CTV-466 follow-up: when the patch changes the date window, re-derive
  // sibling segment shares per the rotation-segment rules so each slot's
  // segmentShares keep partitioning its [startDate, endDate] window. Without
  // this, dragging a bar edge or editing the end-date input leaves the
  // segmentShares pinned to the prior window and the backend rejects save
  // with "segmentShares do not partition its window". Mirrors
  // useCreativeSlots.updateSlot.
  const patchSlot = (slotId: string, patch: Partial<EditSlot>) => {
    setSlots((prev) => {
      const current = prev.find((s) => s.slotId === slotId);
      const willChangeDates =
        (patch.startDate !== undefined && patch.startDate !== current?.startDate) ||
        (patch.endDate !== undefined && patch.endDate !== current?.endDate);
      const patched = prev.map((s) => (s.slotId === slotId ? { ...s, ...patch } : s));
      if (!willChangeDates) return patched;
      const target = patched.find((s) => s.slotId === slotId);
      if (!target) return patched;
      const derived = rederiveSegmentSharesOnDateChange(
        toRotationWindows(prev),
        slotId,
        target.startDate,
        target.endDate,
      );
      return applyDerivedShares(patched, derived);
    });
  };

  const addSlot = () => {
    setSlots((prev) => {
      const slotId = makeSlotId();
      // CTV-487 / ADR-0012: on a running campaign, a new commercial added
      // mid-flight starts today (or campaign start, whichever is later) so
      // the stored window reflects when delivery actually begins. The
      // server applies the same defaulting at save when no explicit
      // startDate is provided — mirroring it here keeps the Gantt truthful
      // pre-save.
      const defaultStart =
        submission.status === 'approved' && campaignStartDate
          ? maxYmd(todayYmd(), campaignStartDate)
          : campaignStartDate;
      const next: EditSlot = {
        slotId,
        existingCreativeId: null,
        existingStatus: null,
        storagePath: null,
        filename: '',
        mimeType: 'video/mp4',
        sizeBytes: 0,
        clickThroughUrl: '',
        segmentShares: singleSegmentShareForWindow(defaultStart, campaignEndDate),
        startDate: defaultStart,
        endDate: campaignEndDate,
      };
      const prospective = [...prev, next];
      const derived = seedSegmentSharesForNewCreative(toRotationWindows(prev), {
        id: slotId,
        startDate: defaultStart,
        endDate: campaignEndDate,
      });
      return applyDerivedShares(prospective, derived);
    });
  };

  // CTV-611 deferred fix slot: no slot is pre-created for a rejection; one is
  // created the moment a file lands through any of the recovery actions
  // (upload / library pick / Waymark rework). The window is seeded from the
  // newest unresolved voided row: startDate = max(today, voided start),
  // endDate = voided end; if the voided window already ended, fall back to
  // today -> campaign end. Everything else (segment-share seeding,
  // validation) behaves exactly like a normal add.
  const addFixSlot = (opts?: { sourceDraftId?: string | null }): string => {
    const slotId = makeSlotId();
    const today = todayYmd();
    let startDate =
      submission.status === 'approved' && campaignStartDate
        ? maxYmd(today, campaignStartDate)
        : campaignStartDate;
    let endDate = campaignEndDate;
    if (fixSource) {
      if (fixSource.creative_end_date < today) {
        startDate = today;
        endDate = campaignEndDate;
      } else {
        startDate = maxYmd(today, fixSource.creative_start_date);
        endDate = fixSource.creative_end_date;
      }
    }
    setSlots((prev) => {
      const next: EditSlot = {
        slotId,
        existingCreativeId: null,
        existingStatus: null,
        storagePath: null,
        filename: '',
        mimeType: 'video/mp4',
        sizeBytes: 0,
        clickThroughUrl: '',
        segmentShares: singleSegmentShareForWindow(startDate, endDate),
        startDate,
        endDate,
        isFix: true,
        sourceDraftId: opts?.sourceDraftId ?? null,
      };
      const prospective = [...prev, next];
      const derived = seedSegmentSharesForNewCreative(toRotationWindows(prev), {
        id: slotId,
        startDate,
        endDate,
      });
      return applyDerivedShares(prospective, derived);
    });
    return slotId;
  };

  const handleFixFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!file) return;
    void upload(file, addFixSlot());
  };

  // CTV-611: minimal Waymark rework client (see useWaymarkRework). The
  // rendered file routes into the same fix-slot upload path, carrying the
  // voided row's source_draft_id so the fresh creative keeps the pointer.
  const rework = useWaymarkRework({
    onFileReady: (file, sourceDraftId) => {
      void upload(file, addFixSlot({ sourceDraftId }));
    },
  });

  // CTV-611: once any new content-bearing slot exists in this session, the
  // recovery block collapses (visibleSlots is already filtered to slots with
  // content). Saving any add stamps resolved_at server-side, so the banner
  // only ever promises what save will do; removing the slot before saving
  // brings the banner back. Fully derived, no sticky state.
  const hasNewContentSlot = React.useMemo(
    () => visibleSlots.some((s) => s.existingCreativeId == null),
    [visibleSlots],
  );

  // CTV-606 (ported in CTV-611): the campaign was already in Paused Delivery
  // when the panel opened, so the Pause banner should describe the state
  // ("currently paused") rather than the transition ("Once you save").
  // Reuses pausedNow (stored-row, active-only coverage of today) instead of
  // CTV-606's separate initial-coverage memo, and yields to the recovery
  // block, which carries its own paused-state line when it is showing.
  const alreadyPaused = pausedNow && (fixSource == null || hasNewContentSlot);

  // CTV-611 rotation note: when a fix-originated slot's window overlaps a
  // still-active slot's window, both will deliver in rotation. Rendered
  // outside the recovery block (which collapses once the fix slot exists).
  // Copy branches on the fix's prior-approval state: a pre-approved pick
  // never enters review and starts running at save (ADR-0007).
  const rotationNote = React.useMemo(() => {
    const fix = visibleSlots.find((s) => s.isFix);
    if (!fix) return null;
    const active = visibleSlots.find(
      (s) =>
        s.existingStatus === 'active' &&
        s.startDate <= fix.endDate &&
        fix.startDate <= s.endDate,
    );
    if (!active) return null;
    const key = `${fix.filename}::${fix.sizeBytes ?? ''}`;
    return {
      activeFilename: active.filename,
      fixFilename: fix.filename,
      preApproved: priorApprovalByKey.get(key) === 'approved',
    };
  }, [visibleSlots, priorApprovalByKey]);

  const removeSlot = (slotId: string) => {
    setSlots((prev) => {
      const idx = prev.findIndex((s) => s.slotId === slotId);
      if (idx < 0) return prev;
      const removed = prev[idx];
      if (removed.existingCreativeId) {
        setRemovedSourceIds((r) => {
          const next = new Set(r);
          next.add(removed.existingCreativeId!);
          return next;
        });
      }
      const remaining = prev.filter((s) => s.slotId !== slotId);
      if (remaining.length === 0) return [];
      const derived = reseatSharesAfterRemoval(toRotationWindows(remaining));
      return applyDerivedShares(remaining, derived);
    });
    setFiles((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
    // CTV-487 follow-up: if the buyer X's a slot whose file is still
    // uploading, clear the upload tracker so Save / Add / Restore don't
    // stay disabled when the orphaned PUT eventually resolves (or hangs).
    setUploadingSlotId((prev) => (prev === slotId ? null : prev));
  };

  // Triggered when CreativeSlotCard's CreativeUploader picks a file. If the
  // slot is currently pointing at an existing creative row, we treat the
  // upload as a replacement — mark the old id as removed and convert this
  // slot to a fresh add.
  const handleSlotFileChange = (slotId: string, file: File | null) => {
    if (!file) {
      setFiles((prev) => {
        const next = { ...prev };
        delete next[slotId];
        return next;
      });
      return;
    }
    void upload(file, slotId);
  };

  const upload = async (file: File, slotId: string) => {
    setError(null);
    setUploadingSlotId(slotId);
    setFiles((prev) => ({ ...prev, [slotId]: file }));
    try {
      const { uploadUrl, storagePath } = await apiClient.post<{
        uploadUrl: string;
        storagePath: string;
      }>('/creatives/upload-url', {
        slug: cfg.slug,
        filename: file.name,
        mimeType: file.type || 'video/mp4',
        sizeBytes: file.size,
      });
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': file.type || 'video/mp4' },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      // CTV-508: generate the server-side poster (non-black thumbnail) now that
      // the swapped-in creative has landed. Best-effort — a failure falls back
      // to the `#t=0.5` <video> seek.
      try {
        await apiClient.post('/creatives/poster', { slug: cfg.slug, storagePath });
      } catch {
        /* poster is decorative — never fail the swap over it */
      }
      setSlots((prev) =>
        prev.map((s) => {
          if (s.slotId !== slotId) return s;
          if (s.existingCreativeId) {
            setRemovedSourceIds((r) => {
              const next = new Set(r);
              next.add(s.existingCreativeId!);
              return next;
            });
          }
          return {
            ...s,
            existingCreativeId: null,
            existingStatus: null,
            storagePath,
            filename: file.name,
            mimeType: file.type || 'video/mp4',
            sizeBytes: file.size,
          };
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploadingSlotId(null);
    }
  };

  const applyLibraryPick = (slotId: string, pick: LibraryPick) => {
    setSlots((prev) =>
      prev.map((s) => {
        if (s.slotId !== slotId) return s;
        if (s.existingCreativeId) {
          setRemovedSourceIds((r) => {
            const next = new Set(r);
            next.add(s.existingCreativeId!);
            return next;
          });
        }
        return {
          ...s,
          existingCreativeId: null,
          existingStatus: null,
          storagePath: pick.storagePath,
          filename: pick.filename,
          mimeType: pick.mimeType,
          sizeBytes: pick.sizeBytes,
          clickThroughUrl: pick.clickThroughUrl,
        };
      }),
    );
    setFiles((prev) => ({ ...prev, [slotId]: null }));
  };

  const onSave = async () => {
    if (!hasChanges) return;
    if (slots.length === 0) {
      setError('Keep at least one commercial.');
      return;
    }
    const missingUploads = slots.filter((s) => !s.storagePath);
    if (missingUploads.length > 0) {
      setError('Every commercial needs a video file.');
      return;
    }
    const addCreatives = slots
      .filter((s) => s.existingCreativeId == null)
      .map((s) => ({
        filename: s.filename,
        storagePath: s.storagePath as string,
        mimeType: s.mimeType,
        sizeBytes: s.sizeBytes,
        clickThroughUrl: s.clickThroughUrl?.trim() || undefined,
        segmentShares: s.segmentShares.map((seg) => ({ ...seg })),
        startDate: s.startDate,
        endDate: s.endDate,
        // CTV-611: Waymark-rework fix slots keep the voided row's draft
        // pointer so the fresh creative can be reopened in Waymark later.
        ...(s.sourceDraftId ? { sourceDraftId: s.sourceDraftId } : {}),
      }));
    const updateExisting = slots
      .filter((s) => s.existingCreativeId != null)
      .map((s) => ({
        creativeId: s.existingCreativeId as string,
        segmentShares: s.segmentShares.map((seg) => ({ ...seg })),
        startDate: s.startDate,
        endDate: s.endDate,
        clickThroughUrl: s.clickThroughUrl?.trim() || undefined,
      }));

    setSaving(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${submission.id}/update-creative-set`, {
        removeCreativeIds: Array.from(removedSourceIds),
        addCreatives,
        updateExisting,
      });
      analytics.track(EVENTS.CREATIVE_SET_UPDATED, {
        station_slug: cfg.slug,
        submission_id: submission.id,
        added: addCreatives.length,
        updated: updateExisting.length,
        removed: removedSourceIds.size,
      });
      onSubmitted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save commercial changes');
    } finally {
      setSaving(false);
    }
  };

  const allCommercialsUploaded = slots.every((s) => !!s.storagePath);
  // Mirrors UploadCreativeStep: the scheduler only renders once at least two
  // slots have content. An empty extra slot doesn't get a bar.
  const showScheduler = visibleSlots.length >= 2;
  // Gate "Add another commercial" so the user can't stack empty upload
  // cards — every existing slot must have content first.
  const canAddSlot = slots.every(slotHasContent);

  return (
    <div className="space-y-5">
      {/* CTV-611 recovery block: one rose banner for all unresolved
          rejections, above the slots. Reviewer notes per rejected file, a
          read-only context card with the three fix actions, and (when
          delivery is paused) the inline Restore line. Collapses as soon as
          the buyer lands any new commercial this session: saving that add
          resolves the rejection, so the alert's job is done. */}
      {fixSource && !hasNewContentSlot && (
        <Banner tone="warn" label="Commercial rejected">
          <div className="space-y-3">
            <ul className="space-y-3">
              {unresolvedRejections.map((c) => (
                <li key={c.id}>
                  <div className="font-medium text-rose-900 mb-0.5">{c.filename}</div>
                  {c.rejection_reason_codes && c.rejection_reason_codes.length > 0 && (
                    <ul className="space-y-0.5">
                      {c.rejection_reason_codes.map((code) => {
                        const r = REJECTION_REASONS_BY_CODE[code];
                        if (!r) return null;
                        return (
                          <li key={code} className="text-rose-800 leading-relaxed">
                            <span className="font-medium text-rose-900">{r.label}</span>
                            {r.advertiserText && (
                              <>
                                {': '}
                                <span className="text-rose-700/90">{r.advertiserText}</span>
                              </>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {c.rejection_reason && (
                    <div className="mt-1 pl-3 border-l-2 border-rose-200 text-rose-800 leading-relaxed italic">
                      "{c.rejection_reason}"
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {/* Read-only rejected-file context card. NOT an EditSlot: the
                voided row is never edited in place and never enters the save
                payload; the actions below create a fresh fix slot instead. */}
            <div className="rounded-lg border border-rose-200 bg-white/70 px-3.5 py-3">
              <div className="text-[12px] font-semibold text-slate-900 tracking-tight truncate">
                {fixSource.filename}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Was scheduled {fixSource.creative_start_date} to {fixSource.creative_end_date}. Send a fixed commercial and {cfg.name} will review it again.
              </div>
              <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                <label
                  className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full text-white cursor-pointer transition-all hover:brightness-110 active:scale-[0.98] ${
                    saving || uploadingSlotId !== null ? 'opacity-50 pointer-events-none' : ''
                  }`}
                  style={{
                    background: `linear-gradient(180deg, ${accent}, ${cfg.primaryColor})`,
                    boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px -8px ${cfg.primaryColor}`,
                  }}
                >
                  Upload a fixed file
                  <input
                    type="file"
                    accept="video/*"
                    className="sr-only"
                    aria-label="Upload a fixed file"
                    onChange={handleFixFileInput}
                    disabled={saving || uploadingSlotId !== null}
                  />
                </label>
                {advertiserId && (
                  <button
                    type="button"
                    onClick={() => setFixPickerOpen(true)}
                    disabled={saving || uploadingSlotId !== null}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-50 transition-all"
                  >
                    Choose from library
                  </button>
                )}
                {fixSource.source_draft_id && !rework.unavailableCreativeIds.has(fixSource.id) && (
                  <button
                    type="button"
                    onClick={() => {
                      void rework.openRework({
                        creativeId: fixSource.id,
                        sourceDraftId: fixSource.source_draft_id as string,
                      });
                    }}
                    disabled={rework.isBusy || saving || uploadingSlotId !== null}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-50 transition-all"
                  >
                    {rework.isBusy && <Loader2 className="w-3 h-3 animate-spin" />}
                    Rework in Waymark
                  </button>
                )}
              </div>
              {rework.unavailableCreativeIds.has(fixSource.id) && (
                <p className="text-[11px] text-rose-700 mt-2">
                  This commercial can't be reopened in Waymark. Upload a fixed file instead.
                </p>
              )}
              {rework.error && <p className="text-[11px] text-rose-700 mt-2">{rework.error}</p>}
            </div>

            {/* Restore, demoted into the recovery block: only offered while
                delivery is actually paused, as the "resume delivering while
                the fix is in review" companion move. */}
            {restorable && pausedNow && (
              <div className="flex items-start justify-between gap-3 text-[12px] text-slate-700">
                <span>
                  Delivery is paused meanwhile. Restore <strong>{restorable.filename}</strong> to
                  resume delivering while your fix is reviewed.
                </span>
                <button
                  type="button"
                  onClick={onRestorePrevious}
                  disabled={saving || uploadingSlotId !== null}
                  className="shrink-0 text-[12px] font-semibold text-rose-700 underline underline-offset-2 hover:text-rose-900 disabled:opacity-50"
                >
                  {saving && <Loader2 className="inline w-3 h-3 animate-spin mr-1" />}
                  Restore
                </button>
              </div>
            )}
          </div>
        </Banner>
      )}

      <p className="text-[13px] text-slate-600">
        Add, replace, remove, or re-time the commercials on this campaign. New uploads go to {cfg.name} for review. If a change leaves no approved commercial running today, the dashboard will warn you before you save.
      </p>

      {/* CTV-611 rotation note: lives outside the recovery block so it
          survives the block's collapse once the fix slot exists. Neutral
          tone: it describes the future schedule, not the rejection. */}
      {rotationNote && (
        <p className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          {rotationNote.preApproved ? (
            <>
              <strong>{rotationNote.fixFilename}</strong> is pre-approved and will start running as
              soon as you save, based on the schedule you enter below.
            </>
          ) : (
            <>
              <strong>{rotationNote.activeFilename}</strong> keeps running while{' '}
              <strong>{rotationNote.fixFilename}</strong> is reviewed. Once approved, both will run
              based on the schedule you enter below.
            </>
          )}
        </p>
      )}

      <div className="space-y-5">
        {slots.map((slot) => {
          const fileForSlot = files[slot.slotId] ?? null;
          const pos = visiblePosition(slot.slotId);
          const hasContent = pos >= 0;
          // Empty slots render as upload-only cards: no scheduler position,
          // no colour-coded dot in the palette, no status pill. CreativeSlotCard
          // surfaces "New commercial" (vs. "Commercial N") when index === -1.
          const color = hasContent ? visibleColors[pos] : '#cbd5e1';
          // Status pill only renders for content-bearing slots. Four states:
          //   • Existing row, status='active' → "Running" (live now).
          //   • Existing row, status='pending_review' → "Awaiting review"
          //     (publisher hasn't decisioned it yet).
          //   • New add whose (filename, sizeBytes) matches an approved
          //     library entry → "Pre-approved" (ADR-0007 prior-approval
          //     mirrors what the server will record on save — no review
          //     queue stop).
          //   • New add without prior approval → "New — awaiting review".
          let statusPill: { label: string; tone: 'neutral' | 'pending' } | undefined;
          if (!hasContent) {
            statusPill = undefined;
          } else if (slot.existingCreativeId != null) {
            statusPill =
              slot.existingStatus === 'pending_review'
                ? { label: 'Awaiting review', tone: 'pending' as const }
                : { label: 'Running', tone: 'neutral' as const };
          } else {
            const key = `${slot.filename}::${slot.sizeBytes ?? ''}`;
            const prior = slot.filename ? priorApprovalByKey.get(key) : undefined;
            statusPill =
              prior === 'approved'
                ? { label: 'Pre-approved', tone: 'neutral' as const }
                : { label: 'New — awaiting review', tone: 'pending' as const };
          }
          return (
            <CreativeSlotCard
              key={slot.slotId}
              slot={slot}
              slug={cfg.slug}
              file={fileForSlot}
              accentColor={accent}
              color={color}
              index={hasContent ? pos : -1}
              removable
              uploadError={null}
              statusPill={statusPill}
              onFileChange={(f) => handleSlotFileChange(slot.slotId, f)}
              onClickThroughUrlChange={(next) => patchSlot(slot.slotId, { clickThroughUrl: next })}
              onRemove={() => removeSlot(slot.slotId)}
              onPickFromLibrary={advertiserId ? () => setPickerSlotId(slot.slotId) : undefined}
            />
          );
        })}
      </div>

      {canAddSlot && (
        <button
          type="button"
          onClick={addSlot}
          disabled={saving || uploadingSlotId !== null}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-dashed border-slate-300 text-[12px] font-semibold tracking-tight text-slate-600 hover:bg-slate-50 hover:border-slate-500 disabled:opacity-40 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add another commercial
        </button>
      )}

      {showScheduler && (
        <MultiCreativeScheduler
          slots={visibleSlots}
          colors={visibleColors}
          campaignStartDate={campaignStartDate}
          campaignEndDate={campaignEndDate}
          accentColor={accent}
          slug={cfg.slug}
          onSlotChange={(slotId, patch) => patchSlot(slotId, patch)}
          onResetWeights={() => {
            setSlots((prev) => applyDerivedShares(prev, resetToEven(toRotationWindows(prev))));
          }}
        />
      )}

      {visibleSlots.length > 0 && !coverage.covered && (
        <CoverageBanner
          uncoveredRanges={coverage.uncoveredRanges}
          surface="swap"
          prefix={
            preExistingGap
              ? 'This campaign has uncovered days. Fix them now to save your changes.'
              : undefined
          }
          slots={visibleSlots.map((s) => ({
            id: s.slotId,
            filename: s.filename,
            startDate: s.startDate,
            endDate: s.endDate,
          }))}
          onExtendLeftCreative={(id, newEnd) => patchSlot(id, { endDate: newEnd })}
          onExtendRightCreative={(id, newStart) => patchSlot(id, { startDate: newStart })}
        />
      )}

      {visibleSlots.length > 0 && !concurrency.withinCap && (
        <ConcurrencyBanner
          overCapRanges={concurrency.overCapRanges}
          prefix={
            preExistingOverCap
              ? 'This campaign already has too many commercials running on these dates. Fix them now to save your changes.'
              : undefined
          }
        />
      )}

      {/* CTV-487 / CTV-606 (ported in CTV-611): Paused Delivery banner. Two
          variants:
          - alreadyPaused: campaign is in Paused Delivery right now (state).
            Copy describes the current state, no "Once you save" wording.
          - willPauseDelivery: the buyer's pending diff will introduce a
            pause on save (transition). Keeps the original "Once you save"
            wording, which reads correctly for a fresh mid-modal edit. */}
      {(alreadyPaused || willPauseDelivery) && (
        <Banner tone="info" label={alreadyPaused ? 'Delivery paused' : 'Delivery pause'}>
          {(() => {
            if (alreadyPaused) {
              const pendingFilename = pendingForRestore[0]?.filename ?? null;
              return pendingFilename
                ? `Your campaign is currently paused, waiting for ${cfg.name} to approve ${pendingFilename}.`
                : `Your campaign is currently paused. Add a commercial or restore your previous one below to resume delivery.`;
            }
            const firstGap = pausedPreviewCoverage.uncoveredRanges[0];
            const today = todayYmd();
            const startsToday = firstGap && firstGap.startDate <= today;
            return startsToday
              ? `Once you save, your campaign's delivery will pause until ${cfg.name} approves your new commercial. Most reviews finish within 1 business day, and delivery resumes automatically.`
              : `Once you save, your campaign's delivery will pause from ${firstGap?.startDate ?? ''} until ${cfg.name} approves the new commercial.`;
          })()}
        </Banner>
      )}

      {/* CTV-487: Restore previous commercial. When the campaign is in
          Paused Delivery and the buyer's most-recently-retired commercial
          is not already in the slot list, offer a single-click restore.
          CTV-611: while a rejection is unresolved, Restore lives inside the
          recovery block above instead, so this standalone banner only
          renders when there is no unresolved rejection. */}
      {/* CTV-611: eligible whenever the recovery block is not showing. That
          includes the fix-in-progress state (hasNewContentSlot): a pending
          fix leaves delivery paused until review, so Restore stays offered
          as the resume-now companion move. */}
      {restorable && (unresolvedRejections.length === 0 || hasNewContentSlot) && (
        <Banner tone="info" label="Restore commercial">
          <div className="flex items-start justify-between gap-3">
            <span>
              Bring back <strong>{restorable.filename}</strong> to resume delivery without waiting for review.
              {pendingForRestore.length > 0 && (
                <>
                  {' '}Your pending commercial{pendingForRestore.length === 1 ? '' : 's'} (
                  <strong>{pendingForRestore.map((c) => c.filename).join(', ')}</strong>
                  {/* CTV-611 / ADR-0023: a removed undecided row now lands
                      status 'abandoned', so the copy says "removed", not
                      "withdrawn" (withdrawn presumes an approval). */}
                  ) will be removed.
                </>
              )}
            </span>
            <button
              type="button"
              onClick={onRestorePrevious}
              disabled={saving || uploadingSlotId !== null}
              className="shrink-0 inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1 rounded-full border border-blue-300 bg-white text-blue-700 hover:bg-blue-50 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Restore
            </button>
          </div>
        </Banner>
      )}

      {error && <Banner tone="warn" label="Couldn't save">{error}</Banner>}

      <div className="flex flex-col items-end gap-1.5 pt-2 border-t border-slate-100">
        {/* CTV-487 follow-up: mirror the extend-campaign / wizard
            inventory-validation pattern — small right-aligned spinner+text
            line directly above the disabled action button so the buyer
            knows exactly why Save isn't clickable. */}
        {uploadingSlotId !== null && (
          <p className="text-[11px] text-slate-500 flex items-center justify-end gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" />
            Uploading video before you save…
          </p>
        )}
        <PrimaryButton
          type="button"
          onClick={onSave}
          disabled={
            !hasChanges ||
            saving ||
            uploadingSlotId !== null ||
            !allCommercialsUploaded ||
            !coverage.covered ||
            !concurrency.withinCap
          }
          accentColor={accent}
          primaryColor={cfg.primaryColor}
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Save changes
        </PrimaryButton>
      </div>

      {(pickerSlotId || fixPickerOpen) && advertiserId && (
        <LibraryPickerModal
          open
          slug={cfg.slug}
          advertiserId={advertiserId}
          onClose={() => {
            setPickerSlotId(null);
            setFixPickerOpen(false);
          }}
          onPick={(pick) => {
            if (pickerSlotId) {
              applyLibraryPick(pickerSlotId, pick);
              setPickerSlotId(null);
            } else {
              // CTV-611 deferred fix slot: created only once the pick lands.
              applyLibraryPick(addFixSlot(), pick);
              setFixPickerOpen(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default EditCommercialTabPanel;
